/* ARIA — floating SMI Field Analyst chat bubble (site-wide, portal). */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const SUGGESTIONS = [
    'What does SMI build for businesses?',
    'How do I spot a deepfake?',
    'What does automation cost?',
    'How does the Registry protect my work?',
];

const sid = () => {
    let s = localStorage.getItem('smi_aria_sid');
    if (!s) { s = `aria_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem('smi_aria_sid', s); }
    return s;
};

const linkify = (text) => text.split(/(\/[a-z0-9\-/#]+|sales@syntheticmediainvestigator\.com)/g).map((part, i) => {
    if (/^\/[a-z0-9\-/#]+$/.test(part) && part.length > 1) return <a key={i} href={part} className="text-lab-blue underline hover:text-white">{part}</a>;
    if (part.includes('@')) return <a key={i} href={`mailto:${part}`} className="text-lab-blue underline hover:text-white">{part}</a>;
    return part;
});

export default function AssistantBubble() {
    const [open, setOpen] = useState(false);
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [teaser, setTeaser] = useState(null);
    const endRef = useRef(null);

    useEffect(() => {
        if (localStorage.getItem('smi_aria_teased') === '1') return undefined;
        let timer = null;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting && !timer) {
                    const where = e.target.getAttribute('data-aria-pricing') || 'pricing';
                    timer = setTimeout(() => {
                        if (localStorage.getItem('smi_aria_teased') === '1') return;
                        localStorage.setItem('smi_aria_teased', '1');
                        setTeaser(where);
                        trackEvent('aria_pricing_teaser', { where });
                    }, 8000);
                } else if (!e.isIntersecting && timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            });
        }, { threshold: 0.35 });
        const attach = () => document.querySelectorAll('[data-aria-pricing]').forEach((el) => io.observe(el));
        attach();
        const iv = setInterval(attach, 3000);
        return () => { clearInterval(iv); io.disconnect(); if (timer) clearTimeout(timer); };
    }, []);

    const openFromTeaser = () => {
        setTeaser(null);
        setOpen(true);
        trackEvent('aria_teaser_open', {});
        setMsgs((m) => (m.length ? m : [{ role: 'assistant', content: "I noticed you're weighing up pricing — happy to help. Tell me what your business does and I'll point you to the right package, or the configurator at /automation-center can price a custom build in about 2 minutes." }]));
    };

    useEffect(() => {
        if (!open) return;
        fetch(`${API}/api/assistant/history/${sid()}`).then((r) => r.json())
            .then((d) => setMsgs((m) => (m.length ? m : (d.messages || []))))
            .catch(() => {});
    }, [open]);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, busy]);

    const ask = async (text) => {
        const q = (text || input).trim();
        if (!q || busy) return;
        setInput('');
        setBusy(true);
        trackEvent('aria_message', { len: q.length });
        setMsgs((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
        try {
            const res = await fetch(`${API}/api/assistant/chat`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sid(), message: q }),
            });
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let buf = '';
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                const events = buf.split('\n\n');
                buf = events.pop();
                for (const ev of events) {
                    const line = ev.split('\n').find((l) => l.startsWith('data: '));
                    if (!line) continue;
                    const payload = JSON.parse(line.slice(6));
                    if (payload.delta) {
                        setMsgs((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + payload.delta };
                            return copy;
                        });
                    }
                }
            }
        } catch {
            setMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', content: 'Connection snag — please try again in a moment.' };
                return copy;
            });
        }
        setBusy(false);
    };

    return createPortal(
        <>
            {!open && teaser && (
                <div data-testid="aria-teaser" className="fixed bottom-[5.5rem] right-5 z-[70] w-72 glass-strong rounded-2xl rounded-br-md border border-lab-blue/40 p-4 shadow-2xl animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex items-start gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-lab-blue to-lab-violet flex items-center justify-center text-black shrink-0"><Sparkles className="w-3.5 h-3.5" /></span>
                        <div className="min-w-0">
                            <div className="lab-mono text-[8px] text-lab-emerald tracking-wider mb-1">ARIA · FIELD ANALYST</div>
                            <p className="text-white/90 text-xs leading-relaxed">Weighing up pricing? I can point you to the right package — or price a custom build in about 2 minutes.</p>
                        </div>
                        <button data-testid="aria-teaser-dismiss" onClick={() => setTeaser(null)} className="text-lab-dim hover:text-white shrink-0 -mt-1 -mr-1 p-1"><X className="w-3 h-3" /></button>
                    </div>
                    <button data-testid="aria-teaser-open" onClick={openFromTeaser}
                        className="mt-3 w-full lab-mono text-[9px] tracking-wider py-2 rounded-full bg-gradient-to-r from-lab-blue to-lab-violet text-black font-semibold hover:opacity-90">ASK ARIA</button>
                </div>
            )}
            {!open && (
                <button data-testid="aria-open" onClick={() => { setOpen(true); trackEvent('aria_open', {}); }}
                    className="fixed bottom-5 right-5 z-[70] w-14 h-14 rounded-full bg-gradient-to-br from-lab-blue to-lab-violet text-black shadow-[0_0_30px_rgba(0,240,255,0.35)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                    aria-label="Chat with ARIA">
                    <MessageCircle className="w-6 h-6" />
                </button>
            )}
            {open && (
                <div data-testid="aria-panel" className="fixed bottom-5 right-5 z-[70] w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-6rem))] glass-strong rounded-3xl border border-lab-blue/30 flex flex-col overflow-hidden shadow-2xl">
                    <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 bg-black/40">
                        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-lab-blue to-lab-violet flex items-center justify-center text-black shrink-0"><Sparkles className="w-4 h-4" /></span>
                        <div className="flex-1 min-w-0">
                            <div className="text-white font-semibold text-sm">ARIA</div>
                            <div className="lab-mono text-[8px] text-lab-emerald tracking-wider">S.M.I. FIELD ANALYST · ONLINE</div>
                        </div>
                        <button data-testid="aria-close" onClick={() => setOpen(false)} className="p-2 rounded-full text-lab-mute hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="aria-messages">
                        {msgs.length === 0 && (
                            <div>
                                <div className="glass rounded-2xl rounded-tl-md p-3 text-sm text-white/90 leading-relaxed">
                                    I'm ARIA — SMI's field analyst. Ask me anything about AI, synthetic media, verification, or what we build. I answer from SMI's own knowledge base.
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {SUGGESTIONS.map((s) => (
                                        <button key={s} data-testid="aria-suggestion" onClick={() => ask(s)}
                                            className="lab-mono text-[9px] px-3 py-1.5 rounded-full border border-lab-blue/30 text-lab-blue hover:bg-lab-blue/10 text-left">{s}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {msgs.map((m, i) => (
                            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                                <div className={m.role === 'user'
                                    ? 'bg-gradient-to-r from-lab-blue/80 to-lab-violet/80 text-black rounded-2xl rounded-tr-md px-3.5 py-2.5 text-sm max-w-[85%] whitespace-pre-wrap'
                                    : 'glass rounded-2xl rounded-tl-md px-3.5 py-2.5 text-sm text-white/90 max-w-[90%] leading-relaxed whitespace-pre-wrap'}>
                                    {m.content ? linkify(m.content) : <Loader2 className="w-4 h-4 animate-spin text-lab-blue" />}
                                </div>
                            </div>
                        ))}
                        <div ref={endRef} />
                    </div>
                    <form className="p-3 border-t border-white/10 flex gap-2 bg-black/30" onSubmit={(e) => { e.preventDefault(); ask(); }}>
                        <input data-testid="aria-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask ARIA anything…"
                            className="flex-1 bg-white/5 border border-white/10 focus:border-lab-blue/50 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-lab-dim outline-none min-w-0" />
                        <button data-testid="aria-send" type="submit" disabled={busy || !input.trim()}
                            className="w-10 h-10 rounded-full bg-gradient-to-r from-lab-blue to-lab-violet text-black flex items-center justify-center disabled:opacity-40 shrink-0">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                    </form>
                </div>
            )}
        </>,
        document.body
    );
}
